/**
 * `Spark` — spec §F2 `Spark(series, w, h, color, unit, activeIdx)`; the mockup's
 * `sparkSVG()` with three additions the real data forces.
 *
 * 1. **Nulls break the line.** §D3 returns `null` for a year with no data (`/series`), and
 *    the mockup — written against a dense fixture — had no case for it. A polyline drawn
 *    straight across a gap is a fabricated trend, so the series is split into runs of
 *    consecutive present points: one polyline (and one area) per run, and an isolated point
 *    is drawn as a dot with no line at all.
 * 2. **An area fill** under each run at 13% of the series colour, so a 300×70 chart reads
 *    as a magnitude and not only as a slope.
 * 3. **Hover through the shared `ChartTip` portal** (§F2), rather than the mockup's SVG
 *    `<title>` — a native tooltip cannot be styled, waits a second before appearing, and is
 *    clipped by nothing but also positioned by nothing.
 *
 * The colour is the series colour only; every number on screen is an ink token (§F2: "text
 * in ink tokens (never series colour for values)") — here that means the tooltip, which is
 * the only place this chart prints a value.
 */
import { useMemo } from 'react';

import { fmtNum } from '../../lib/format';
import { hideChartTip, showChartTip } from './chartTipStore';

/** The mockup's inner padding — keeps the r=4 active dot off the viewBox edge. */
export const SPARK_PAD = 6;

export interface SparkProps {
  /** One value per period; `null`/`undefined`/non-finite = no data for that period. */
  series: readonly (number | null | undefined)[];
  w?: number;
  h?: number;
  color?: string;
  /** Display unit, appended to the tooltip value (`12.3 producers`). */
  unit?: string;
  /** Index of the period the rest of the app is showing — drawn filled, with a ring. */
  activeIdx?: number;
  /** X labels, normally the §F1 years. Defaults to the 1-based position. */
  labels?: readonly (string | number)[];
  /** §G3 decimals for the tooltip value. */
  fmt?: number;
  /** Screen-reader description of the whole chart. */
  ariaLabel?: string;
  className?: string;
}

interface Point {
  i: number;
  v: number;
  x: number;
  y: number;
}

/** Consecutive present points, in order — the segments that may legitimately be joined. */
function runsOf(points: readonly Point[]): Point[][] {
  const runs: Point[][] = [];
  let current: Point[] = [];
  for (const p of points) {
    const last = current[current.length - 1];
    if (last && p.i !== last.i + 1) {
      runs.push(current);
      current = [];
    }
    current.push(p);
  }
  if (current.length) runs.push(current);
  return runs;
}

export function Spark({
  series,
  w = 300,
  h = 70,
  color = '#2E7D32',
  unit = '',
  activeIdx = -1,
  labels,
  fmt = 2,
  ariaLabel,
  className,
}: SparkProps) {
  const { points, runs } = useMemo(() => {
    const n = series.length;
    const present: Point[] = [];
    let min = Infinity;
    let max = -Infinity;
    for (const v of series) {
      if (typeof v === 'number' && Number.isFinite(v)) {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }
    // A single distinct value has no range to scale against: the mockup parks it at half
    // height, which reads as "flat" rather than as an arbitrary top or bottom.
    const X = (i: number) => (n < 2 ? w / 2 : SPARK_PAD + (i * (w - 2 * SPARK_PAD)) / (n - 1));
    const Y = (v: number) =>
      h - SPARK_PAD - (max === min ? 0.5 : (v - min) / (max - min)) * (h - 2 * SPARK_PAD);

    series.forEach((v, i) => {
      if (typeof v === 'number' && Number.isFinite(v)) present.push({ i, v, x: X(i), y: Y(v) });
    });
    return { points: present, runs: runsOf(present) };
  }, [series, w, h]);

  const label = (i: number): string => String(labels?.[i] ?? i + 1);
  const valueText = (v: number): string => `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  return (
    <svg
      className={`chart spark${className ? ` ${className}` : ''}`}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label={ariaLabel ?? `Trend, ${points.length} of ${series.length} periods reported`}
      data-points={points.length}
      onMouseLeave={hideChartTip}
    >
      {runs.map((run) =>
        run.length > 1 ? (
          <polygon
            key={`area-${run[0]?.i}`}
            data-spark-area=""
            points={`${run[0]?.x},${h} ${run.map((p) => `${p.x},${p.y}`).join(' ')} ${
              run[run.length - 1]?.x
            },${h}`}
            fill={color}
            fillOpacity={0.13}
            stroke="none"
          />
        ) : null,
      )}
      {runs.map((run) =>
        run.length > 1 ? (
          <polyline
            key={`line-${run[0]?.i}`}
            data-spark-line=""
            points={run.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null,
      )}
      {points.map((p) => {
        const active = p.i === activeIdx;
        return (
          <g key={p.i} data-spark-point={p.i} {...(active ? { 'data-active': '' } : {})}>
            {active ? (
              <circle
                cx={p.x}
                cy={p.y}
                r={7}
                fill="none"
                stroke={color}
                strokeOpacity={0.35}
                strokeWidth={1.6}
                data-spark-ring=""
              />
            ) : null}
            <circle
              cx={p.x}
              cy={p.y}
              r={active ? 4 : 2.4}
              fill={active ? color : '#fff'}
              stroke={color}
              strokeWidth={1.6}
            />
            {/* The hit target is bigger than the dot and invisible — 2.4px is not a
                pointer target, and §F2's tooltip has to be reachable on every point. */}
            <circle
              cx={p.x}
              cy={p.y}
              r={9}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              data-spark-hit={p.i}
              data-value={valueText(p.v)}
              onMouseEnter={(e) => showChartTip(e, label(p.i), valueText(p.v))}
              onMouseMove={(e) => showChartTip(e, label(p.i), valueText(p.v))}
              onMouseLeave={hideChartTip}
            />
          </g>
        );
      })}
    </svg>
  );
}

export default Spark;
